import { TuiDocument } from '../core/Document';
import { Grid } from '../core/Grid';

/**
 * Frame-based animation.
 *
 * A frame is a set of visible layers, not a copy of the canvas. That choice
 * matters: shared artwork (a border, a background) lives on one layer that
 * every frame includes, so a ten-frame animation does not cost ten copies of
 * the whole screen — and editing the border once updates every frame.
 */

export interface AnimationFrame {
  id: string;
  name: string;
  /** Layer ids composited for this frame, bottom to top. */
  layers: string[];
  /** Overrides the animation's frame duration when set. */
  durationMs?: number;
}

export interface Animation {
  id: string;
  name: string;
  fps: number;
  loop: boolean;
  /** Play forwards then backwards instead of jumping back to frame 1. */
  pingPong: boolean;
  frames: AnimationFrame[];
}

export const FPS_PRESETS = [5, 10, 15, 24, 30, 60] as const;

let counter = 0;

export function newAnimation(name = 'Animation'): Animation {
  counter += 1;
  return {
    id: `anim-${Date.now().toString(36)}-${counter.toString(36)}`,
    name,
    fps: 10,
    loop: true,
    pingPong: false,
    frames: [],
  };
}

export function newFrame(layers: string[], name?: string): AnimationFrame {
  counter += 1;
  return {
    id: `frame-${Date.now().toString(36)}-${counter.toString(36)}`,
    name: name ?? `Frame ${counter}`,
    layers: [...layers],
  };
}

export function frameDurationMs(animation: Animation, frame: AnimationFrame): number {
  return frame.durationMs ?? Math.round(1000 / Math.max(1, Math.min(60, animation.fps)));
}

export function totalDurationMs(animation: Animation): number {
  return animation.frames.reduce((sum, f) => sum + frameDurationMs(animation, f), 0);
}

/** The playback order, accounting for ping-pong. */
export function playbackOrder(animation: Animation): AnimationFrame[] {
  if (!animation.pingPong || animation.frames.length < 3) return animation.frames;
  // Endpoints are not repeated, so a 4-frame ping-pong is 1,2,3,4,3,2.
  return [...animation.frames, ...animation.frames.slice(1, -1).reverse()];
}

/** Index into `playbackOrder` at a point in time. */
export function frameIndexAt(animation: Animation, elapsedMs: number): number {
  const order = playbackOrder(animation);
  if (order.length === 0) return -1;
  const total = order.reduce((sum, f) => sum + frameDurationMs(animation, f), 0);
  if (total <= 0) return 0;
  let t = elapsedMs;
  if (animation.loop) t = ((t % total) + total) % total;
  else if (t >= total) return order.length - 1;

  let acc = 0;
  for (let i = 0; i < order.length; i++) {
    acc += frameDurationMs(animation, order[i]);
    if (t < acc) return i;
  }
  return order.length - 1;
}

/** Composites one frame into a grid. */
export function renderFrame(doc: TuiDocument, frame: AnimationFrame): Grid {
  return doc.compositeOf(frame.layers);
}

/** Drops layer ids that no longer exist, so deleting a layer cannot corrupt frames. */
export function pruneFrames(animation: Animation, existingLayerIds: readonly string[]): Animation {
  return {
    ...animation,
    frames: animation.frames.map((f) => ({
      ...f,
      layers: f.layers.filter((id) => existingLayerIds.includes(id)),
    })),
  };
}
